import { Head } from '@inertiajs/react'
import { useState } from 'react'
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import SyntaxHighlighter from 'react-syntax-highlighter'
import { atomOneDark } from 'react-syntax-highlighter/dist/esm/styles/hljs'
import { DocumentationToggle } from '@/components/documentation-toggle'
import { IconCopy, IconCheck, IconKey } from '@tabler/icons-react'
import { toast } from 'sonner'
import singleFileWidgetImage from '/assets/single_uploader.png'
import multiFileWidgetImage from '/assets/multi-file-uploader.png'

export default function QuickStart({ bucket_uuid }) {
  const [selectedLang, setSelectedLang] = useState('JavaScript');
  const [copiedCode, setCopiedCode] = useState(null);
  
  // Use the actual bucket UUID if logged in, otherwise show placeholder
  const publicKey = bucket_uuid || 'YourPublicKey';
  
  const codeStyle = {
    background: '#21252b',
    padding: '1rem',
    margin: 0,
    fontSize: '.8rem',
    borderRadius: '6px'
  };

  const handleCopy = async (code, codeId) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(codeId);
      toast.success('Code copied to clipboard!', {
        duration: 2000
      });
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      toast.error('Failed to copy code', {
        duration: 2000
      });
    }
  };

  const CodeBlock = ({ language, code, codeId, customStyle }) => (
    <div className="relative group">
      <SyntaxHighlighter
        language={language}
        style={atomOneDark}
        customStyle={customStyle || codeStyle}
      >
        {code}
      </SyntaxHighlighter>
      <Button
        variant="secondary"
        size="icon"
        className="absolute top-2 right-2 h-8 w-8 bg-white/90 hover:bg-white text-gray-700 border border-gray-200"
        onClick={() => handleCopy(code, codeId)}
      >
        {copiedCode === codeId ? (
          <IconCheck className="h-4 w-4" />
        ) : (
          <IconCopy className="h-4 w-4" />
        )}
      </Button>
    </div>
  );

  return (
    <>
      <DocumentationToggle />
      
      <div className="container mx-auto py-6 px-4 max-w-6xl">
        <Head title="Quick Start Guide - Simple File Upload" />
        
        <h1 className="text-5xl font-bold mb-12">Quick Start Guide</h1>
        
        {/* Upload Key Callout for logged-in users */}
        {bucket_uuid && (
          <Card className="mb-12 border-2 border-blue-500 bg-blue-50 dark:bg-blue-950/30">
            <CardHeader>
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-500 rounded-full">
                  <IconKey className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-xl mb-2">Your Upload Key</CardTitle>
                  <div className="flex items-center gap-3">
                    <code className="px-3 py-2 bg-white dark:bg-gray-900 rounded-md border border-blue-200 dark:border-blue-800 text-lg font-mono">
                      {bucket_uuid}
                    </code>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCopy(bucket_uuid, 'upload-key')}
                      className="border-blue-500 text-blue-600 hover:bg-blue-100"
                    >
                      {copiedCode === 'upload-key' ? (
                        <>
                          <IconCheck className="h-4 w-4 mr-2" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <IconCopy className="h-4 w-4 mr-2" />
                          Copy
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                    This is your public upload key. Use it in the code examples below.
                  </p>
                </div>
              </div>
            </CardHeader>
          </Card>
        )}
        
        {/* Step 1: Upload */}
        <Card className="mb-12">
          <CardHeader className="gap-6">
            <div className="flex items-center gap-4">
              <Badge variant="secondary" className="h-10 w-10 rounded-full flex items-center justify-center text-xl">1</Badge>
              <CardTitle className="text-2xl">Add Javascript</CardTitle>
            </div>
            <CardDescription className="text-xl">
              Simple File Upload should be quick and easy to get up and running.
              <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-md border border-blue-200 dark:border-blue-900">
                <span className="text-base">
                  If you have any trouble, drop us a line at{' '}
                  <a href="mailto:support@simplefileupload.com" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 underline font-medium">
                    support@simplefileupload.com
                  </a>
                  {' '}— we have the best customer support around.
                </span>
              </div>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-lg">Install the JavaScript library:</p>
              <CodeBlock
                language="html"
                code={`<script type="module" src="https://cdn.simplefileupload.com/simple-file-upload.latest.js"></script>`}
                codeId="install-script"
              />
              
              <p className="mt-4 text-lg">Add the web component to your page:</p>
              <CodeBlock
                language="xml"
                code={`<simple-file-upload 
  public-key="${publicKey}"
></simple-file-upload>
`}
                codeId="web-component"
              />
            </div>
          </CardContent>
        </Card>

        {/* Step 2: Customize */}
        <Card className="mb-8">
          <CardHeader className="gap-6">
            <div className="flex items-center gap-4">
              <Badge variant="secondary" className="h-10 w-10 rounded-full flex items-center justify-center text-xl">2</Badge>
              <CardTitle className="text-2xl">Customize</CardTitle>
            </div>
            <CardDescription className="text-xl">Configure the uploader with your preferred settings</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-lg">Customize the uploader behavior with these attributes:</p>
              <ul className="list-disc pl-6 space-y-2 text-lg">
                <li><code className="rounded bg-muted px-2 py-1 text-lg">multiple="true|false"</code> -  Show the multi-file uploader (default) or the single-file uploader</li>
                <li><code className="rounded bg-muted px-2 py-1 text-lg">max-files="number"</code> - Set maximum number of files</li>
                <li><code className="rounded bg-muted px-2 py-1 text-lg">max-file-size="number"</code> - Set maximum file size in bytes</li>
              </ul>
              
              <div className="mt-6 space-y-8">
                <div>
                  <h3 className="text-xl font-medium mb-2">Single File Upload</h3>
                  <p className="text-muted-foreground text-lg mb-3">Perfect for profile pictures or single document uploads</p>
                  <div className="overflow-hidden rounded-lg border-2 border-gray-200 shadow-md max-w-md">
                    <img 
                      src={singleFileWidgetImage} 
                      alt="Single File Upload Widget" 
                      className="w-full h-auto"
                    />
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-medium mb-2">Multi-File Upload</h3>
                  <p className="text-muted-foreground text-lg mb-3">Great for batch uploads and galleries</p>
                  <div className="overflow-hidden rounded-lg border-2 border-gray-200 shadow-md max-w-md">
                    <img 
                      src={multiFileWidgetImage} 
                      alt="Multi-File Upload Widget" 
                      className="w-full h-auto"
                    />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step 3: Event Handling */}
        <Card className="mb-8">
          <CardHeader className="gap-6">
            <div className="flex items-center gap-4">
              <Badge variant="secondary" className="h-10 w-10 rounded-full flex items-center justify-center text-xl">3</Badge>
              <CardTitle className="text-2xl">Handle Events</CardTitle>
            </div>
            <CardDescription className="text-xl">Listen for upload events and handle file data</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-xl font-medium mb-3">Add event listeners to handle file uploads:</p>
              <CodeBlock
                language="javascript"
                code={`<script>
  // Listen for change events on the uploader
  document.addEventListener('DOMContentLoaded', function() {
    const uploader = document.querySelector('simple-file-upload');
    uploader.addEventListener('change', function(e) {
      console.log('Upload details:', e.detail);
      // Handle the uploaded files
      const files = e.detail.allFiles;
      // Your code here
    });
  });
</script>`}
                codeId="event-handling"
              />
            </div>
          </CardContent>
        </Card>

        {/* Step 4: Serve Files */}
        <Card className="mb-8">
          <CardHeader className="gap-6">
            <div className="flex items-center gap-4">
              <Badge variant="secondary" className="h-10 w-10 rounded-full flex items-center justify-center text-xl">4</Badge>
              <CardTitle className="text-2xl">Serve Files</CardTitle>
            </div>
            <CardDescription className="text-xl">Resize your images on demand </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <p className="text-xl font-medium mb-3">How do I resize on demand?</p>
                <p className="text-lg mb-4">Easy! Simply add query parameters for width(<code className="text-lg rounded bg-muted px-1">w</code>) and height(<code className="text-lg rounded bg-muted px-1">h</code>). Add <code className="text-lg rounded bg-muted px-2 py-1">?w=20&h=20</code> to the end of your image URL to resize to 20 by 20.</p>
              </div>
              
              <div>
                <p className="text-xl font-medium mb-3">Example</p>
                <div className="mb-4">
                  <div className="inline-flex rounded-lg p-1 bg-muted text-sm">
                    {['JavaScript', 'Ruby', 'PHP', 'Shell'].map(lang => (
                      <button
                        key={lang}
                        className={`px-3 py-1.5 rounded-md transition-colors ${
                          selectedLang === lang
                            ? 'bg-white text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                        onClick={() => setSelectedLang(lang)}
                      >
                        {lang}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="relative">
                  <div className={selectedLang === 'JavaScript' ? 'block' : 'hidden'}>
                    <CodeBlock
                      language="javascript"
                      code={`// Original URL
const { cdnUrl } = event.detail.allFiles[0];  // From the upload event handler

// Resized to 200x200
const resized = cdnUrl + '?w=200&h=200';

// Using in an img tag
<img src={cdnUrl + '?w=300&h=300'} alt="Resized image" />`}
                      codeId="resize-js"
                    />
                  </div>

                  <div className={selectedLang === 'Ruby' ? 'block' : 'hidden'}>
                    <CodeBlock
                      language="ruby"
                      code={`# Original URL
cdn_url = @upload.cdn_url  # Saved in your database

# Resized to 200x200
resized_url = "#{cdn_url}?w=200&h=200"

# In ERB template
<%= image_tag "#{cdn_url}?w=300&h=300", alt: 'Resized image' %>`}
                      codeId="resize-ruby"
                    />
                  </div>

                  <div className={selectedLang === 'PHP' ? 'block' : 'hidden'}>
                    <CodeBlock
                      language="php"
                      code={`// Original URL
$cdnUrl = $upload->cdn_url;  // Saved in your database

// Resized to 200x200
$resized = $cdnUrl . '?w=200&h=200';

// In HTML
echo '<img src="' . $cdnUrl . '?w=300&h=300" alt="Resized image">';`}
                      codeId="resize-php"
                    />
                  </div>

                  <div className={selectedLang === 'Shell' ? 'block' : 'hidden'}>
                    <CodeBlock
                      language="bash"
                      code={`# Download original image
CDN_URL="https://your-saved-cdn-url.jpg"  # Replace with your saved URL

# Download original image
curl $CDN_URL > original.jpg

# Download resized image (200x200)
curl "$CDN_URL?w=200&h=200" > resized.jpg

# Using wget
wget "$CDN_URL?w=300&h=300" -O resized.jpg`}
                      codeId="resize-shell"
                    />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  )
}
